// src/App.js
import React, { useState, useEffect } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from './services/firebase';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Navigation from './components/Navigation';
import HomePage from './components/HomePage';
import TipstersPage from './components/TipstersPage';
import BettingTracker from './components/BettingTracker';
import DegenSystem from './components/DegenSystem';
import WellnessPage from './components/WellnessPage';
import PricingPage from './components/PricingPage';
import LoginPage from './components/LoginPage';

const App = () => {
  const [currentPage, setCurrentPage] = useState('home');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const unsubscribe = onAuthStateChanged(auth, user => {
        setUser(user);
        setLoading(false);
      }, error => {
        console.error('Auth state error:', error);
        toast.error('Failed to initialize authentication. Please try again.');
        setLoading(false);
      });
      return () => unsubscribe();
    } catch (error) {
      console.error('Firebase initialization error:', error);
      toast.error('Failed to connect to Firebase. Please check your configuration.');
      setLoading(false);
    }
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setCurrentPage('home');
      toast.success('Logged out successfully!');
    } catch (error) {
      console.error('Error logging out:', error);
      toast.error('Failed to log out. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ToastContainer position="top-right" autoClose={3000} />
      <Navigation
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        user={user}
        handleLogout={handleLogout}
      />
      <div className="pt-16">
        {currentPage === 'home' && <HomePage setCurrentPage={setCurrentPage} />}
        {currentPage === 'tipsters' && <TipstersPage user={user} />}
        {currentPage === 'tracker' && <BettingTracker user={user} />}
        {currentPage === 'degen-system' && <DegenSystem />}
        {currentPage === 'wellness' && <WellnessPage />}
        {currentPage === 'pricing' && <PricingPage setCurrentPage={setCurrentPage} user={user} />}
        {currentPage === 'login' && <LoginPage setCurrentPage={setCurrentPage} />}
      </div>
      <footer className="bg-gray-800 text-white py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4">PureDegen</h3>
              <p className="text-gray-400">Professional bet tracking for serious sports bettors.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-3">Quick Links</h4>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => setCurrentPage('home')} className="hover:text-white">Home</button></li>
                <li><button onClick={() => setCurrentPage('tipsters')} className="hover:text-white">Tipsters</button></li>
                <li><button onClick={() => setCurrentPage('tracker')} className="hover:text-white">Bet Tracker</button></li>
                <li><button onClick={() => setCurrentPage('degen-system')} className="hover:text-white">Degen System</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-3">Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => setCurrentPage('wellness')} className="hover:text-white">Wellness</button></li>
                <li><button onClick={() => setCurrentPage('pricing')} className="hover:text-white">Pricing</button></li>
                <li><a href="#" className="hover:text-white">FAQ</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-3">Connect</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="https://discord.gg/se6RA6Ge" target="_blank" rel="noopener noreferrer" className="hover:text-white">Discord: Join PureDegen</a></li>
                <li><a href="https://instagram.com/PureDegen" target="_blank" rel="noopener noreferrer" className="hover:text-white">Instagram: @PureDegen</a></li>
                <li><a href="mailto:support@puredegen.com" className="hover:text-white">support@puredegen.com</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <p>© 2025 PureDegen. All rights reserved. Gamble responsibly.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;